import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@tanstack/react-query';
import {
  AnyExternalLead,
  CdqExternalLead,
  CdqExternalLeadRequest,
  ExternalLeadProduct,
  ExternalLeadStatus,
  LoanExternalLead,
  LoanExternalLeadRequest,
  MortgageExternalLead,
} from '@tassomutuo-backoffice/types';
import { formatNumberWithThousandsSeparator } from '../../../../utils/formatMoney';
import {
  getCdqExternalLead,
  getLoanExternalLead,
  getMortgageExternalLead,
} from '../../../../services/api/external-leads/external-leads';
import useDateFormat from '../../../../hooks/useDateFormat';
import { DetailFieldList, DetailField } from '@tassomutuo-backoffice/ui';
import ExternalLeadDataTree from './ExternalLeadDataTree';
import ExternalLeadStatusChip from './ExternalLeadStatusChip';
import ExternalLeadQualityChip from './ExternalLeadQualityChip';

interface IProps {
  open: boolean;
  product: ExternalLeadProduct;
  leadId: string | null;
  onClose: () => void;
}

const modalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '85%', md: '75%', lg: '65%' },
  maxWidth: 900,
  maxHeight: '90vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 1,
  p: 3,
};

const SectionHeader = ({ title }: { title: string }) => (
  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
    {title}
  </Typography>
);

const fullPhone = (prefix?: string, phone?: string) =>
  prefix && phone ? `${prefix} ${phone}` : prefix || phone || undefined;

const yesNo = (value?: boolean) =>
  value === undefined ? undefined : value ? 'Sì' : 'No';

const fetchLead = async (
  product: ExternalLeadProduct,
  id: string
): Promise<AnyExternalLead> => {
  if (product === 'mortgage') return getMortgageExternalLead(id);
  if (product === 'loan') return getLoanExternalLead(id);
  return getCdqExternalLead(id);
};

const useExternalLead = (product: ExternalLeadProduct, id: string | null) => {
  return useQuery<AnyExternalLead>({
    queryKey: ['externalLead', product, id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Missing id'));
      return fetchLead(product, id);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

const buildMortgageSections = (
  lead: MortgageExternalLead,
  formatDate: (d: string | number | Date, f?: string) => string
) => {
  const contatto: DetailField[] = [
    { label: 'Nome', value: lead.name },
    { label: 'Cognome', value: lead.lastName },
    { label: 'Email', value: lead.email },
    { label: 'Telefono', value: fullPhone(lead.prefix, lead.phone) },
    { label: 'Consenso TOC', value: yesNo(lead.toc) },
  ];
  const sorgente: DetailField[] = [
    { label: 'Agenzia', value: lead.agenzia_euroansa_da_cui_arriva_la_lead },
    { label: 'Consulente', value: lead.consulente_richiesto_pagina },
    { label: 'Segnalatore', value: lead.segnalatore },
    { label: 'Comune', value: lead.comune },
    { label: 'Provincia', value: lead.provincia },
    { label: 'Regione', value: lead.regione },
    { label: 'CAP', value: lead.cap },
  ];
  return { contatto, sorgente };
};

const OCCUPATION_LABEL: Record<string, string> = {
  dipendente_pubblico: 'Dipendente Statale',
  dipendente_privato: 'Dipendente Privato',
  dipendente_parapubblico: 'Dipendente Parastatale',
  pensionato: 'Pensionato',
  autonomo: 'Lavoratore Autonomo',
};

const formatYesNo = (v?: string) =>
  v === 'si' ? 'Sì' : v === 'no' ? 'No' : v;

const buildCdqRequestFields = (req?: CdqExternalLeadRequest): DetailField[] => {
  if (!req) return [];

  const occupazione = req.occupazione
    ? OCCUPATION_LABEL[req.occupazione] ?? req.occupazione
    : undefined;

  const contratto = (() => {
    if (req.indeterminato === 'si') return 'Tempo indeterminato';
    if (req.indeterminato === 'no') return 'Tempo determinato';
    return undefined;
  })();

  return [
    {
      label: 'Importo richiesto',
      value: req.importo
        ? `€ ${formatNumberWithThousandsSeparator(String(req.importo))}`
        : undefined,
    },
    {
      label: 'Stipendio',
      value: req.stipendio
        ? `€ ${formatNumberWithThousandsSeparator(String(req.stipendio))}`
        : undefined,
    },
    { label: 'Mensilità', value: req.mensilita },
    { label: 'Occupazione', value: occupazione },
    { label: 'Contratto', value: contratto },
    { label: 'Anzianità lavorativa', value: req.tempo },
    { label: 'N° dipendenti azienda', value: req.nDipendenti },
    {
      label: 'Età',
      value: req.anni !== undefined && req.anni !== null ? String(req.anni) : undefined,
    },
    {
      label: 'Reddito mensile pensionato',
      value: req.redditoMensilePensionato
        ? `€ ${formatNumberWithThousandsSeparator(
            String(req.redditoMensilePensionato)
          )}`
        : undefined,
    },
    { label: 'Cittadinanza italiana', value: formatYesNo(req.cittadinanzaItaliana) },
    { label: 'SPID', value: formatYesNo(req.spid) },
    { label: 'Altri prestiti in corso', value: formatYesNo(req.altriPrestitiInCorso) },
    { label: 'Ritardi di pagamento', value: formatYesNo(req.ritardiPagamenti) },
    {
      label: 'Approfondisci richiesto',
      value:
        req.approfondisci === undefined
          ? undefined
          : req.approfondisci
            ? 'Sì'
            : 'No',
    },
    { label: 'Consulenza', value: req.consulenza },
    {
      label: 'Località',
      value:
        req.comune || req.provincia
          ? `${req.cap ? req.cap + ' ' : ''}${req.comune ?? ''}${
              req.provincia ? ` (${req.provincia})` : ''
            }${req.regione ? ' · ' + req.regione : ''}`.trim()
          : undefined,
    },
  ];
};

const buildLoanRequestFields = (req?: LoanExternalLeadRequest): DetailField[] => {
  if (!req) return [];
  return [
    {
      label: 'Importo richiesto',
      value:
        req.loanAmount !== undefined && req.loanAmount !== null
          ? `€ ${formatNumberWithThousandsSeparator(String(req.loanAmount))}`
          : undefined,
    },
    {
      label: 'Reddito mensile',
      value:
        req.monthlyIncome !== undefined && req.monthlyIncome !== null
          ? `€ ${formatNumberWithThousandsSeparator(String(req.monthlyIncome))}`
          : undefined,
    },
    { label: 'Finalità', value: req.loanPurpose },
    { label: 'Contratto', value: req.employmentContract },
    { label: 'Anni in azienda', value: req.yearsCurrentCompany },
    { label: 'Altri finanziamenti', value: formatYesNo(req.hasOtherFinancing) },
    { label: 'Ritardi su prestiti', value: formatYesNo(req.hasLatePreviousLoan) },
    {
      label: 'Località',
      value:
        req.comune || req.provincia
          ? `${req.cap ? req.cap + ' ' : ''}${req.comune ?? ''}${
              req.provincia ? ` (${req.provincia})` : ''
            }${req.regione ? ' · ' + req.regione : ''}`.trim()
          : undefined,
    },
  ];
};

const buildLoanOrCdqSections = (
  lead: LoanExternalLead,
  formatDate: (d: string | number | Date, f?: string) => string
) => {
  const contatto: DetailField[] = [
    { label: 'Nome', value: lead.name },
    { label: 'Cognome', value: lead.lastname },
    { label: 'Email', value: lead.email },
    { label: 'Telefono', value: fullPhone(lead.prefix, lead.phone) },
    { label: 'Consenso TOC', value: yesNo(lead.toc) },
  ];
  const sorgente: DetailField[] = [
    { label: 'Sorgente', value: lead.from },
    { label: 'Agenzia', value: lead.agenzia_euroansa_da_cui_arriva_la_lead },
    { label: 'Consulente', value: lead.consulente_richiesto_pagina },
    { label: 'Segnalatore', value: lead.segnalatore },
  ];
  return { contatto, sorgente };
};

const ExternalLeadDetailModal = ({
  open,
  product,
  leadId,
  onClose,
}: IProps) => {
  const { formatDate } = useDateFormat();
  const { data, isLoading, error } = useExternalLead(product, leadId);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error || !data) {
      return (
        <Alert severity="error">
          Impossibile caricare il dettaglio della lead esterna.
        </Alert>
      );
    }

    const lead = data;
    const status = lead.status as ExternalLeadStatus;
    const quality = (lead as { quality?: string }).quality;

    const { contatto, sorgente } =
      product === 'mortgage'
        ? buildMortgageSections(lead as MortgageExternalLead, formatDate)
        : buildLoanOrCdqSections(lead as LoanExternalLead, formatDate);

    const mortgageLead =
      product === 'mortgage' ? (lead as MortgageExternalLead) : null;
    const loanLike = product !== 'mortgage' ? (lead as LoanExternalLead) : null;
    const cdqLead = product === 'cdq' ? (lead as CdqExternalLead) : null;

    const techFields: DetailField[] = [
      { label: 'ID', value: lead.id },
      {
        label: 'Creata il',
        value: lead.createdAt
          ? formatDate(lead.createdAt, 'dd/MM/yyyy HH:mm')
          : undefined,
      },
      {
        label: 'Aggiornata il',
        value: lead.updatedAt
          ? formatDate(lead.updatedAt, 'dd/MM/yyyy HH:mm')
          : undefined,
      },
    ];
    if (cdqLead?.quotationId) {
      techFields.push({ label: 'Quotation ID', value: cdqLead.quotationId });
    }

    return (
      <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ExternalLeadStatusChip status={status} />
          <ExternalLeadQualityChip quality={quality} />
        </Stack>

        <Box>
          <SectionHeader title="Contatto" />
          <DetailFieldList fields={contatto} />
        </Box>

        <Divider />

        <Box>
          <SectionHeader title="Sorgente" />
          <DetailFieldList fields={sorgente} />
        </Box>

        <Divider />

        <Box>
          <SectionHeader title="Dati funnel" />
          {mortgageLead && (
            <Stack spacing={2}>
              {mortgageLead.product && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Prodotto bancario
                  </Typography>
                  <ExternalLeadDataTree data={mortgageLead.product} />
                </Box>
              )}
              {mortgageLead.estimatorData && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Preventivatore
                  </Typography>
                  <ExternalLeadDataTree data={mortgageLead.estimatorData} />
                </Box>
              )}
              {!mortgageLead.product && !mortgageLead.estimatorData && (
                <Typography variant="body2" color="text.secondary">
                  Nessun dato funnel disponibile.
                </Typography>
              )}
            </Stack>
          )}
          {loanLike && product === 'loan' && (
            <Stack spacing={2}>
              {loanLike.request ? (
                <DetailFieldList
                  fields={buildLoanRequestFields(loanLike.request)}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessun dato funnel disponibile.
                </Typography>
              )}
            </Stack>
          )}
          {cdqLead && (
            <Stack spacing={2}>
              {cdqLead.request && (
                <DetailFieldList
                  fields={buildCdqRequestFields(cdqLead.request)}
                />
              )}
              {cdqLead.selected && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Offerta selezionata
                  </Typography>
                  <ExternalLeadDataTree data={cdqLead.selected as Record<string, unknown>} />
                </Box>
              )}
              {!cdqLead.request && !cdqLead.selected && (
                <Typography variant="body2" color="text.secondary">
                  Nessun dato funnel disponibile.
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, borderTop: (th) => `1px solid ${th.palette.divider}` }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Dati tecnici
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Stack spacing={2}>
              <DetailFieldList fields={techFields} />
              {lead.checkResults && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Check FIDO
                  </Typography>
                  <ExternalLeadDataTree data={lead.checkResults} />
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Dettaglio lead esterna
          </Typography>
          <IconButton onClick={onClose}>
            <ClearIcon color="primary" />
          </IconButton>
        </Box>
        {renderBody()}
      </Box>
    </Modal>
  );
};

export default ExternalLeadDetailModal;
